import React from 'react';
import { fieldClass } from '@/components/UI/fieldStyles';

interface Props {
  status: string;
  priority: string;
  taskType: string;
  dueDateFrom: string;
  dueDateTo: string;
  overdue: boolean;
  onStatusChange: (v: string) => void;
  onPriorityChange: (v: string) => void;
  onTaskTypeChange: (v: string) => void;
  onDueDateFromChange: (v: string) => void;
  onDueDateToChange: (v: string) => void;
  onOverdueChange: (v: boolean) => void;
}

const idleCls = 'flex-shrink-0 flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-medium transition-colors border-ui-border bg-ui-subtle text-ui-text3 hover:border-accent/30 hover:text-ui-text hover:bg-ui-hover';
const activeCls = (color: string) => `flex-shrink-0 flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-semibold transition-colors ring-1 ring-offset-1 ring-current/20 ${color}`;
const allActiveCls = 'bg-accent-fill border-accent text-on-accent';

const CheckIcon = () => (
  <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-2.5 h-2.5 flex-shrink-0">
    <polyline points="2,6 5,9 10,3" />
  </svg>
);

const Chip: React.FC<{
  label: string;
  isActive: boolean;
  activeColor?: string;
  dot?: string;
  onClick: () => void;
}> = ({ label, isActive, activeColor, dot, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={isActive ? activeCls(activeColor ?? allActiveCls) : idleCls}
  >
    {isActive && <CheckIcon />}
    {!isActive && dot && <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />}
    {label}
  </button>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-start gap-3 py-2 border-b border-ui-border/50 last:border-0">
    <span className="text-micro font-semibold text-ui-text3 uppercase tracking-wider w-14 flex-shrink-0 pt-0.5">
      {label}
    </span>
    <div className="flex flex-wrap gap-1">{children}</div>
  </div>
);

/** Multi-select chips hold a comma-separated list ("high,urgent"). */
export /** Toggle one value in a comma-separated multi-select. */
function toggleCsv(list: string, value: string): string {
  const parts = list.split(',').map((s) => s.trim()).filter(Boolean);
  return (parts.includes(value) ? parts.filter((p) => p !== value) : [...parts, value]).join(',');
}

const has = (list: string, value: string) => list.split(',').map((s) => s.trim()).includes(value);

const dateInputCls = `${fieldClass({ size: 'xs' })} !w-auto`;

const TaskListFilters: React.FC<Props> = ({
  status, priority, taskType, dueDateFrom, dueDateTo, overdue,
  onStatusChange, onPriorityChange, onTaskTypeChange,
  onDueDateFromChange, onDueDateToChange, onOverdueChange,
}) => {
  const today = new Date().toISOString().split('T')[0];

  const handleOverdue = () => {
    if (overdue) {
      onOverdueChange(false);
      onDueDateFromChange('');
      onDueDateToChange('');
    } else {
      onOverdueChange(true);
      onDueDateFromChange('');
      onDueDateToChange(today);
    }
  };

  const handleDateChange = (field: 'from' | 'to', val: string) => {
    if (overdue) onOverdueChange(false);
    if (field === 'from') onDueDateFromChange(val);
    else onDueDateToChange(val);
  };

  return (
    <div className="divide-y-0">
      {/* Status */}
      <Row label="Status">
        <Chip label="All"       isActive={status === ''}          onClick={() => onStatusChange('')} />
        <Chip label="Open"      isActive={has(status, 'open')}      activeColor="bg-accent/10 border-accent/30 text-accent-text"   onClick={() => onStatusChange(toggleCsv(status, 'open'))} />
        <Chip label="Complete" isActive={has(status, 'completed')} activeColor="bg-success/10 border-success/30 text-success-text" onClick={() => onStatusChange(toggleCsv(status, 'completed'))} />
        <Chip label="Closed"   isActive={has(status, 'cancelled')} activeColor="bg-ui-hover border-ui-border3 text-ui-text2"  onClick={() => onStatusChange(toggleCsv(status, 'cancelled'))} />
      </Row>

      {/* Priority */}
      <Row label="Priority">
        <Chip label="All"    isActive={priority === ''}       onClick={() => onPriorityChange('')} />
        <Chip label="Low"    isActive={has(priority, 'low')}    dot="bg-steel"  activeColor="bg-ui-subtle border-ui-border3 text-ui-text2"    onClick={() => onPriorityChange(toggleCsv(priority, 'low'))} />
        <Chip label="Normal" isActive={has(priority, 'normal')} dot="bg-accent"  activeColor="bg-accent/10 border-accent/30 text-accent-text"    onClick={() => onPriorityChange(toggleCsv(priority, 'normal'))} />
        <Chip label="High"   isActive={has(priority, 'high')}   dot="bg-warning" activeColor="bg-warning/10 border-warning text-warning-text" onClick={() => onPriorityChange(toggleCsv(priority, 'high'))} />
        <Chip label="Urgent" isActive={has(priority, 'urgent')} dot="bg-danger"   activeColor="bg-danger/10 border-danger text-danger-text"       onClick={() => onPriorityChange(toggleCsv(priority, 'urgent'))} />
      </Row>

      {/* Type */}
      <Row label="Type">
        <Chip label="All"       isActive={taskType === ''}          onClick={() => onTaskTypeChange('')} />
        <Chip label="General"   isActive={taskType === 'general'}   onClick={() => onTaskTypeChange('general')} />
        <Chip label="Follow up" isActive={taskType === 'follow_up'} onClick={() => onTaskTypeChange('follow_up')} />
        <Chip label="Approval"  isActive={taskType === 'approval'}  onClick={() => onTaskTypeChange('approval')} />
        <Chip label="Review"    isActive={taskType === 'review'}    onClick={() => onTaskTypeChange('review')} />
      </Row>

      {/* Due date */}
      <Row label="Due">
        <Chip
          label="Overdue"
          isActive={overdue}
          activeColor="bg-danger/10 border-danger text-danger-text"
          onClick={handleOverdue}
        />
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5 ml-1">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-ui-text3">From</span>
            <input
              type="date"
              value={dueDateFrom}
              disabled={overdue}
              onChange={(e) => handleDateChange('from', e.target.value)}
              className={`${dateInputCls} disabled:opacity-40`}
            />
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-ui-text3">to</span>
            <input
              type="date"
              value={dueDateTo}
              disabled={overdue}
              onChange={(e) => handleDateChange('to', e.target.value)}
              className={`${dateInputCls} disabled:opacity-40`}
            />
          </div>
        </div>
      </Row>
    </div>
  );
};

export default TaskListFilters;
